"use client";

import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, HelpCircle, MessageCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SEO } from "@/components/seo";
import { FAQAnswer } from "@/components/faq";
import { useGetPublicFAQsQuery } from "@/store";
import { Link } from "react-router-dom";

export default function FAQScreen() {
  const [searchTerm, setSearchTerm] = useState("");
  const [openItems, setOpenItems] = useState<string[]>([]);
  const { data: faqData, isLoading, error } = useGetPublicFAQsQuery();

  const categories = faqData?.data || [];

  // Filter FAQs based on search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) return categories;

    const term = searchTerm.toLowerCase();
    return categories
      .map((category) => ({
        ...category,
        faqs: category.faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(term) ||
            faq.answer.toLowerCase().includes(term)
        ),
      }))
      .filter((category) => category.faqs.length > 0);
  }, [categories, searchTerm]);

  // Count total FAQs
  const totalFAQs = useMemo(
    () => categories.reduce((acc, cat) => acc + cat.faqs.length, 0),
    [categories]
  );

  // Prepare FAQ data for structured data
  const faqStructuredData = useMemo(() => {
    return categories.flatMap((cat) =>
      cat.faqs.map((faq) => ({
        question: faq.question,
        answer: faq.answer,
      }))
    );
  }, [categories]);

  // Expand all function for search results
  const handleExpandAll = () => {
    const allFaqIds = filteredCategories.flatMap((cat) =>
      cat.faqs.map((faq) => faq.id)
    );
    setOpenItems(allFaqIds);
  };

  const handleCollapseAll = () => {
    setOpenItems([]);
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="text-center mb-12">
          <Skeleton className="h-12 w-64 mx-auto mb-4" />
          <Skeleton className="h-6 w-96 mx-auto" />
        </div>
        <div className="max-w-3xl mx-auto space-y-8">
          {[1, 2, 3].map((i) => (
            <div key={i}>
              <Skeleton className="h-8 w-48 mb-4" />
              <div className="space-y-3">
                {[1, 2, 3].map((j) => (
                  <Skeleton key={j} className="h-14 w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="text-center">
          <HelpCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Unable to load FAQs</h1>
          <p className="text-muted-foreground">
            Please try again later or contact support.
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (categories.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <HelpCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-3xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-muted-foreground mb-8">
            Our FAQ section is being updated. Check back soon!
          </p>
          <Link to="/contact">
            <Button>
              <MessageCircle className="mr-2 h-4 w-4" />
              Contact Us
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-12 md:py-16">
      <SEO
        title="FAQ - Food Delivery & Catering in Lagos"
        description="Find answers to common questions about our food delivery services in Lagos, our ordering process, catering in Lagos, and private chef bookings."
        canonical="/faq"
        faq={faqStructuredData.length > 0 ? faqStructuredData : undefined}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "FAQ", url: "/faq" },
        ]}
      />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 p-3 mb-4">
          <HelpCircle className="h-8 w-8 text-orange-500" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Frequently Asked Questions
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Find answers to common questions about our services, ordering, delivery,
          and more. Can't find what you're looking for? Feel free to contact us!
        </p>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="max-w-xl mx-auto mb-8"
      >
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search FAQs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 h-12 text-base"
          />
        </div>
        {searchTerm && (
          <div className="flex items-center justify-between mt-3 text-sm">
            <span className="text-muted-foreground">
              {filteredCategories.reduce((acc, cat) => acc + cat.faqs.length, 0)}{" "}
              result(s) found
            </span>
            <div className="space-x-2">
              <Button variant="ghost" size="sm" onClick={handleExpandAll}>
                Expand All
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCollapseAll}>
                Collapse All
              </Button>
            </div>
          </div>
        )}
      </motion.div>

      {/* FAQ Categories */}
      <div className="max-w-3xl mx-auto space-y-8">
        {filteredCategories.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No results found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search terms or browse all categories below.
            </p>
            <Button variant="outline" onClick={() => setSearchTerm("")}>
              Clear Search
            </Button>
          </motion.div>
        ) : (
          filteredCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 + categoryIndex * 0.1 }}
            >
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-500" />
                    {category.name}
                  </CardTitle>
                  {category.description && (
                    <p className="text-sm text-muted-foreground">
                      {category.description}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  <Accordion
                    type="multiple"
                    value={openItems}
                    onValueChange={setOpenItems}
                    className="space-y-2"
                  >
                    {category.faqs.map((faq, faqIndex) => (
                      <motion.div
                        key={faq.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: 0.2 + categoryIndex * 0.1 + faqIndex * 0.05,
                        }}
                      >
                        <AccordionItem
                          value={faq.id}
                          className="border rounded-lg px-4 data-[state=open]:bg-muted/50"
                        >
                          <AccordionTrigger className="text-left hover:no-underline py-4">
                            <span className="font-medium pr-4">{faq.question}</span>
                          </AccordionTrigger>
                          <AccordionContent className="pb-4">
                            <FAQAnswer content={faq.answer} />
                          </AccordionContent>
                        </AccordionItem>
                      </motion.div>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Still have questions section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="mt-16"
      >
        <Card className="max-w-3xl mx-auto bg-gradient-to-br from-orange-500/5 to-amber-500/5 border-orange-200/50 dark:border-orange-900/50">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 p-3 mb-4">
                <MessageCircle className="h-6 w-6 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Still have questions?</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Can't find the answer you're looking for? Our friendly team is here
                to help you with any questions.
              </p>
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Contact Support
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Quick Stats */}
      {totalFAQs > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 text-center"
        >
          <p className="text-sm text-muted-foreground">
            Browse {totalFAQs} FAQs across {categories.length} categories
          </p>
        </motion.div>
      )}
    </div>
  );
}
